// Busca dos dados e chamada da edge do Relatório de Créditos.
//
// A prévia do modal é montada NO NAVEGADOR com a mesma função que a edge usa
// (`montarRelatorioCreditos`, em _shared/) — é isso que faz a tela de conferência
// valer: o operador aprova exatamente os números que vão sair impressos.

import { supabase } from './supabase';
import { ucCodigoKeys } from './ucCodigo';
import { limparUcGeradora, montarRelatorioCreditos, ordemDoMes } from './relatorioCreditos';
import type { FaturaRelatorioCreditos, RelatorioCreditos } from './relatorioCreditos';
import { montarRelatorioFinanceiro } from './relatorioCreditosFinanceiro';
import type { FaturaFinanceira, RelatorioFinanceiro } from './relatorioCreditosFinanceiro';

/** Espelha `CAMPOS_FATURA` da edge. Se mexer numa lista, mexa na outra: a prévia
 *  precisa enxergar os mesmos campos que o PDF, ou o operador aprova outra coisa. */
const CAMPOS_FATURA = `
  id, mes_referencia, data_vencimento, uc_codigo,
  unidade_geradora_1, unidade_geradora_2, unidade_geradora_3, unidade_geradora_4, unidade_geradora_5,
  excedente_recebido_unidade_geradora_1, excedente_recebido_unidade_geradora_2,
  excedente_recebido_unidade_geradora_3, excedente_recebido_unidade_geradora_4,
  excedente_recebido_unidade_geradora_5,
  descricao_ug_1, descricao_ug_2, descricao_ug_3, descricao_ug_4, descricao_ug_5,
  injecao_kwh_ug_1, injecao_kwh_ug_2, injecao_kwh_ug_3, injecao_kwh_ug_4, injecao_kwh_ug_5,
  credito_recebido, saldo_ugs_vigor, consumo_ativo_kwh,
  valor_fatura_distribuidora, valor_boleto_vigor, economia,
  custo_s_vigor, consumo_total_gd_s_vigor, valor_adc_bandeira_s_vigor,
  valor_consumo_scee_boleto, valor_adc_bandeira_boleto,
  valor_parc_injet_s_desc_boleto, valor_restituicao_imposto_boleto,
  valor_energia_nao_isenta_boleto,
  valor_juros_boleto, outros_multas_boleto, valor_parcelamentos_boleto,
  valor_pagamento,
  qtd_injecao_scee_boleto, valor_tarifa_distribuidora, desconto_tarifa,
  tarifa_bandeira, desconto_bandeira, consumo_scee_tarifa_c_imposto,
  tarifa_injecao_gd1, valor_restituicao_imposto,
  nome_cliente_boleto, cpf_cnpj_cliente_boleto,
  status_boleto_vigor, status_fatura, vencimento_boleto, data_pgto_boleto,
  quitado_em, cancelado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh,
  valor_emissao_boleto_unico, ambiente
`;

export interface FaturaDaPrevia extends FaturaRelatorioCreditos, FaturaFinanceira {
  id?: number;
  ambiente?: string | null;
}

export interface PreviaRelatorio {
  relatorio: RelatorioCreditos;
  /** O bloco em reais: contribuição, cobranças, pagamentos e saldo em aberto. */
  financeiro: RelatorioFinanceiro;
  /** Todos os meses da UC, antes do recorte de período — alimenta os seletores. */
  mesesDisponiveis: string[];
  faturas: FaturaDaPrevia[];
}

/**
 * As UCs das usinas da Vigor. `null` quando não deu para saber (RLS, rede, cadastro
 * vazio): nesse caso todo excedente conta como da Vigor e o relatório avisa.
 *
 * Cache de módulo porque a lista muda uma vez por trimestre e o modal abre e fecha
 * várias vezes na mesma sessão. Mesmo motivo (e mesma fonte) de
 * views/Gestao/Extracao/planilha/ucsGeradorasVigor.ts.
 */
let cacheUcsVigor: Promise<Set<string> | null> | null = null;

export function carregarUcsVigor(): Promise<Set<string> | null> {
  if (!cacheUcsVigor) {
    cacheUcsVigor = (async () => {
      const { data, error } = await supabase.from('rateio_unidades_geradoras').select('numero_uc_geradora');
      if (error) {
        console.warn('[relatorio-creditos] não foi possível carregar as usinas da Vigor:', error.message);
        return null;
      }
      const ucs = new Set<string>();
      for (const l of data || []) {
        const uc = limparUcGeradora((l as { numero_uc_geradora: string | null }).numero_uc_geradora);
        if (uc) ucs.add(uc);
      }
      // Cadastro sem nenhuma UC é o mesmo que não saber.
      return ucs.size ? ucs : null;
    })().catch(() => null);
  }
  return cacheUcsVigor;
}

/** Recorte padrão: os 12 últimos meses com fatura. Um relatório de dois anos vira
 *  uma parede de tabela que ninguém confere. */
export const MESES_PADRAO = 12;

export async function carregarPrevia(
  numeroUc: string,
  outroNumeroUc?: string | null,
  periodo?: { inicio?: string | null; fim?: string | null }
): Promise<PreviaRelatorio> {
  const chaves = ucCodigoKeys(numeroUc, outroNumeroUc);
  if (!chaves.length) {
    return {
      relatorio: montarRelatorioCreditos([], null),
      financeiro: montarRelatorioFinanceiro([]),
      mesesDisponiveis: [],
      faturas: [],
    };
  }

  const [{ data, error }, ucsVigor] = await Promise.all([
    supabase.from('faturas').select(CAMPOS_FATURA).in('uc_codigo_norm', chaves),
    carregarUcsVigor(),
  ]);
  if (error) throw new Error(`Não foi possível carregar as faturas desta unidade: ${error.message}`);

  // Emissão de teste não entra em documento entregue ao associado.
  const todas = ((data || []) as FaturaDaPrevia[]).filter(f => f.ambiente !== 'sandbox');

  const mesesDisponiveis = Array.from(
    new Set(todas.map(f => String(f.mes_referencia ?? '')).filter(Boolean))
  ).sort((a, b) => (ordemDoMes(a) ?? 0) - (ordemDoMes(b) ?? 0));

  const de = periodo?.inicio ? ordemDoMes(periodo.inicio) : null;
  const ate = periodo?.fim ? ordemDoMes(periodo.fim) : null;
  const faturas = todas.filter(f => {
    const o = ordemDoMes(f.mes_referencia);
    // Mês irreconhecível entra e sai marcado, em vez de sumir num filtro que não
    // sabe onde ele cai.
    if (o === null) return true;
    return (de === null || o >= de) && (ate === null || o <= ate);
  });

  const relatorio = montarRelatorioCreditos(faturas, ucsVigor);

  // O financeiro fala dos MESMOS ciclos que a energia: uma fatura por mês, a que o
  // relatório de créditos elegeu. Sem isto a prévia mostraria a contribuição de um
  // ciclo de JUL e o crédito de outro — a UC 404028401208 tem três faturas nesse mês.
  const mesesDoRelatorio = new Set(relatorio.linhas.map(l => l.mesReferencia));
  const vistos = new Set<string>();
  const faturasFinanceiras = faturas.filter(f => {
    const m = String(f.mes_referencia ?? '');
    if (!mesesDoRelatorio.has(m) || vistos.has(m)) return false;
    vistos.add(m);
    return true;
  });

  return {
    relatorio,
    financeiro: montarRelatorioFinanceiro(faturasFinanceiras),
    mesesDisponiveis,
    faturas,
  };
}

/**
 * Gera o PDF na edge e devolve os bytes.
 *
 * Vai por fetch e não por `supabase.functions.invoke` de propósito: o invoke escolhe
 * como ler a resposta pelo Content-Type e só trata 'application/octet-stream' como
 * binário. Com 'application/pdf' ele cairia em response.text() e corromperia o
 * arquivo — mesmo motivo de `gerarPdfBoletoVigor` em src/lib/gatewayResolver.ts.
 */
export async function gerarPdfRelatorioCreditos(params: {
  unidadeId?: number | string | null;
  ucCodigo: string;
  mesInicio?: string | null;
  mesFim?: string | null;
}): Promise<Blob> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada. Entre novamente.');

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gerar-relatorio-creditos`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      unidadeId: params.unidadeId ?? null,
      ucCodigo: params.ucCodigo,
      mesInicio: params.mesInicio ?? null,
      mesFim: params.mesFim ?? null,
    }),
  });

  if (!res.ok) {
    // A function devolve JSON no erro e PDF no sucesso.
    const msg = await res.json().catch(() => null);
    throw new Error(msg?.error || `Falha ao gerar o relatório (status ${res.status}).`);
  }
  return res.blob();
}
